/**
 * New console — keyboard shortcuts.
 *
 * Only shortcuts that actually exist. A help screen listing keys that do nothing is
 * worse than no help screen, so every row here is bound somewhere in the console and
 * says where, because a key that works on one screen and not another is confusing
 * unless the scope is stated. The "go to" rows are printed from the navigation table
 * the shell binds them from, and only for screens this role can open.
 *
 * Bound in: NewAppLayout (the global keys and chords), pages/Triage (the queue),
 * CommandPalette, Tour and Help's tutorial (their own arrows), Overlay (Esc).
 */
import { CHORDS } from '../navigation';
import { roleCan } from '../capabilities';
import { Modal } from './Overlay';
import { Button } from './Primitives';

const GENERAL = [
  { keys: 'Ctrl K', what: 'Open the command palette', where: 'Anywhere' },
  { keys: '?', what: 'Show this list', where: 'Anywhere' },
  { keys: '/', what: 'Jump to the search box', where: 'Any screen with one' },
  { keys: '[', what: 'Collapse or expand the navigation', where: 'Desktop' },
  { keys: 'T', what: 'Switch between light and dark', where: 'Anywhere' },
  { keys: 'Esc', what: 'Close a dialog, panel, the palette or the tour', where: 'Anywhere' }
];

const TRIAGE = [
  { keys: '1  2  3', what: 'Answer New patient: Yes, Yes (Referral), No', where: 'Triage queue' },
  { keys: 'S', what: 'Skip this lead without deciding', where: 'Triage queue' },
  { keys: 'N', what: 'Next lead, once this one is triaged', where: 'Triage queue' },
  { keys: 'Ctrl Enter', what: 'Save the note and the answer', where: 'Triage queue, in the note box' }
];

const LISTS = [
  { keys: '↑ ↓', what: 'Move through results', where: 'Command palette' },
  { keys: 'Enter', what: 'Open the highlighted result', where: 'Command palette' },
  { keys: '← →', what: 'Move between steps', where: 'Tour, Getting started' }
];

const chordRows = (role) => Object.keys(CHORDS)
  .map((k) => CHORDS[k])
  .filter((r) => roleCan(role, r.cap))
  .map((r) => ({ keys: `G then ${r.key.toUpperCase()}`, what: `Go to ${r.label}`, where: 'Anywhere' }));

const Rows = ({ title, rows }) => (
  <>
    <div className="nt-eyebrow nt-mt-12">{title}</div>
    <table className="nt-tbl" style={{ minWidth: 0, width: '100%' }}>
      <tbody>
        {rows.map((s) => (
          <tr key={s.keys}>
            <td style={{ width: 110 }}><span className="nt-kbd">{s.keys}</span></td>
            <td className="nt-ink-2">{s.what}</td>
            <td className="n" style={{ color: 'var(--nt-muted)', fontSize: '.72rem' }}>{s.where}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </>
);

const Shortcuts = ({ role, onClose }) => (
  <Modal
    title="Keyboard shortcuts"
    onClose={onClose}
    footer={<><span className="nt-spacer" /><Button variant="primary" onClick={onClose}>Close</Button></>}
  >
    <Rows title="Everywhere" rows={GENERAL} />
    <Rows title="Go to a screen" rows={chordRows(role)} />
    {roleCan(role, 'leads.triage') ? <Rows title="Triage queue" rows={TRIAGE} /> : null}
    <Rows title="Lists and steps" rows={LISTS} />
  </Modal>
);

export default Shortcuts;
